import React, { FC } from 'react';
import { Mutation, MutationFunction } from 'react-apollo';
import { Formik, FormikProps, FormikHelpers } from 'formik';

import {
  UpdateClientDepartmentInput,
  UpdateClientDepartmentMutationFn,
  UpdateClientLocationInput,
  UpdateClientLocationMutationFn,
} from 'api';
import { useNotify } from 'hooks';
import { DepartmentLocationSchema } from 'features/admin/validations';
import { IDepartmentsLocationsOptions } from 'features/admin/types';

import UpdateForm from './UpdateForm';

type IDepartmentLocation = Partial<UpdateClientDepartmentInput | UpdateClientLocationInput>;
type HandleSave = [MutationFunction, IDepartmentLocation, FormikHelpers<IDepartmentLocation>];

interface IProps extends IDepartmentsLocationsOptions {
  item: IDepartmentLocation;
  inputPlaceholderKey: string;
  confirmDeleteKey: string;
  onCompleted: () => void;
}

const Update: FC<IProps> = props => {
  const notify = useNotify();
  const {
    updateResponse,
    updateMutation,
    deleteReducer,
    activeStatus,
    disabledStatus,
    onCompleted,
    item,
    ...rest
  } = props;

  const initialValues = {
    id: item.id,
    title: item.title,
  };

  function handleSave(...args: HandleSave) {
    const [mutation, values] = args;
    const data = { ...values, ...activeStatus };

    return mutation({
      variables: { data },
      optimisticResponse: updateResponse({ ...item, ...data }),
    });
  }

  function handleDelete(mutation: MutationFunction, formikProps: FormikProps<IDepartmentLocation>) {
    const { values } = formikProps;
    const data = { ...values, ...disabledStatus };

    return mutation({
      variables: { data },
      optimisticResponse: updateResponse({ ...item, ...data }),
      update: deleteReducer,
    });
  }

  return (
    <Mutation mutation={updateMutation} onError={notify.mutationError} onCompleted={onCompleted}>
      {(mutation: UpdateClientLocationMutationFn | UpdateClientDepartmentMutationFn) => (
        <Formik
          initialValues={initialValues}
          onSubmit={(values, formikActions) => handleSave(mutation, values, formikActions)}
          validationSchema={DepartmentLocationSchema}
          render={formikProps => (
            <UpdateForm
              {...rest}
              item={item}
              onDelete={() => handleDelete(mutation, formikProps)}
            />
          )}
        />
      )}
    </Mutation>
  );
};

export default Update;
